/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { FlexColumn, FlexRow, Typography } from 'design-system';
import {
  LockIcon,
  PlayCircleIcon,
  FileAltIcon,
} from 'design-system/components/Icons';

const ICON_MAP = {
  video: PlayCircleIcon,
  document: FileAltIcon,
  lock: LockIcon,
};

function InvolvedIcon({ type, locked }) {
  const IconComponent = ICON_MAP[type] || FileAltIcon;

  return (
    <FlexRow
      align="center"
      justify="center"
      fullWidth={false}
      className={[
        'ld-whats-involved__icon',
        locked ? 'ld-whats-involved__icon--locked' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-hidden
    >
      <IconComponent size={18} />
    </FlexRow>
  );
}

InvolvedIcon.propTypes = {
  type: PropTypes.string.isRequired,
  locked: PropTypes.bool,
};

function InvolvedItem({ item }) {
  const intl = useIntl();

  let meta = item.meta;
  if (item.id === 'questionnaire') {
    const passMarkLabel = intl.formatMessage(
      { id: 'ld-whats-involved-pass-mark' },
      { passMark: item.passMarkPercent },
    );

    if (item.questionCount > 0) {
      const countLabel = intl.formatMessage(
        { id: 'ld-question-count' },
        { count: item.questionCount },
      );
      meta = `${countLabel} · ${passMarkLabel}`;
    } else {
      meta = passMarkLabel;
    }
  }

  return (
    <FlexRow
      align="center"
      fullWidth
      gap={14}
      className="ld-whats-involved__item"
    >
      <InvolvedIcon type={item.icon} locked={item.locked} />
      <FlexColumn
        align="start"
        fullWidth={false}
        gap="var(--ds-space-1)"
        className="ld-whats-involved__item-text"
      >
        <Typography
          component="span"
          variant="bodyMedium"
          fontWeight="semibold"
          color="ui-emphasis"
        >
          {item.title}
        </Typography>
        <Typography component="span" variant="bodySmall" color="ui-subtle">
          {meta}
        </Typography>
      </FlexColumn>
    </FlexRow>
  );
}

InvolvedItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    meta: PropTypes.string,
    questionCount: PropTypes.number,
    passMarkPercent: PropTypes.number,
    icon: PropTypes.string.isRequired,
    locked: PropTypes.bool,
  }).isRequired,
};

export default function WhatsInvolved({ items, heading }) {
  const intl = useIntl();
  const resolvedHeading = heading ?? intl.formatMessage({ id: 'ld-whats-involved' });

  if (!items?.length) {
    return null;
  }

  return (
    <FlexColumn
      align="stretch"
      fullWidth
      className="ld-surface ld-whats-involved"
    >
      <FlexRow align="center" fullWidth className="ld-whats-involved__header">
        <Typography
          component="h2"
          variant="bodyMedium"
          fontWeight="semibold"
          color="ui-emphasis"
        >
          {resolvedHeading}
        </Typography>
      </FlexRow>
      <FlexColumn align="stretch" fullWidth className="ld-whats-involved__list">
        {items.map((item) => (
          <InvolvedItem key={item.id} item={item} />
        ))}
      </FlexColumn>
    </FlexColumn>
  );
}

WhatsInvolved.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      meta: PropTypes.string,
      questionCount: PropTypes.number,
      passMarkPercent: PropTypes.number,
      icon: PropTypes.string.isRequired,
      locked: PropTypes.bool,
    }),
  ),
  heading: PropTypes.string,
};
